import type { CreateAbsenceBody, CreateAbsenceResponse } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'
import {
  ABSENCE_CREATE_QUERY_KEY,
  ABSENCES_PAST_QUERY_KEY,
  ABSENCES_QUERY_KEY,
  ABSENCES_TODAY_QUERY_KEY,
  ABSENCES_UPCOMING_QUERY_KEY,
  ABSENCES_URL
} from './queryConstants'

type UseAbsenceCreateProps = {
  onCreateSuccess?: () => void
}

type UseAbsenceCreateReturn = {
  createAbsence: (values: CreateAbsenceBody) => Promise<unknown>
  isCreateLoading: boolean
}

export const useAbsenceCreate = ({
  onCreateSuccess
}: UseAbsenceCreateProps): UseAbsenceCreateReturn => {
  const queryClient = useQueryClient()
  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg) as string,
      type: 'error'
    })
  }

  // CREATE
  const createFunction = async (values: CreateAbsenceBody) => {
    return postJSON({
      url: `${ABSENCES_URL}`,
      method: 'POST',
      json: values,
      isRota: true
    })
  }

  const { mutateAsync: createMutation, isLoading: isCreateLoading } =
    useMutation<CreateAbsenceResponse, unknown, CreateAbsenceBody, unknown>(
      [ABSENCE_CREATE_QUERY_KEY],
      createFunction,
      {
        onSuccess: () => {
          onCreateSuccess?.()
          queryClient.invalidateQueries(ABSENCES_QUERY_KEY)
          queryClient.invalidateQueries(ABSENCES_PAST_QUERY_KEY)
          queryClient.invalidateQueries(ABSENCES_TODAY_QUERY_KEY)
          queryClient.invalidateQueries(ABSENCES_UPCOMING_QUERY_KEY)
        },
        onError: (err) => handleError(err)
      }
    )

  const createAbsence = async (values: CreateAbsenceBody) => {
    try {
      const result = await createMutation(values)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    createAbsence,
    isCreateLoading
  }
}
